import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NoteCaptureResponse.style';

import { Note } from 'shiftNotes/_types';

import NoteCaptureResponseCard from './NoteCaptureResponseCard';

export interface NoteCaptureResponseProps {
  notes: Note[];
  onClose: () => void;
}

export default function NoteCaptureResponse({
  notes,
  onClose,
}: NoteCaptureResponseProps): JSX.Element {
  const shiftNotesCapture = usePosthogCapture();
  const hasCapturedExpandedRef = useRef(false);
  const isExpandedRef = useRef(false);
  const noteCountRef = useRef(notes.length);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasMultipleNotes = notes.length > 1;

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    noteCountRef.current = notes.length;
  }, [notes.length]);

  useEffect(
    () => () => {
      shiftNotesCapture.captureResponseClosed({
        note_count: noteCountRef.current,
        was_expanded: isExpandedRef.current,
      });
    },
    [shiftNotesCapture]
  );

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
    if (isExpanded && !hasCapturedExpandedRef.current) {
      hasCapturedExpandedRef.current = true;
      shiftNotesCapture.captureResponseExpanded({ note_count: notes.length });
    }
  }, [isExpanded, notes.length, shiftNotesCapture]);

  const handleCardClick = useCallback(
    (clickedIndex: number) => {
      if (isExpanded) return;
      if (hasMultipleNotes && clickedIndex !== 0) {
        setIsExpanded(true);
      }
    },
    [isExpanded, hasMultipleNotes]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (isExpanded) {
        e.stopPropagation();
        setIsExpanded(false);
      }
    },
    [isExpanded]
  );

  return (
    <Styled.Container
      ref={containerRef}
      $isExpanded={isExpanded}
      onClick={handleContainerClick}
    >
      <Styled.CardStack $isExpanded={isExpanded}>
        {notes.map((note, index) => (
          <NoteCaptureResponseCard
            key={note.id}
            note={note}
            isActive={isExpanded || index === 0}
            stackPosition={index}
            isExpanded={isExpanded}
            onClose={onClose}
            onClick={
              hasMultipleNotes && !isExpanded && index !== 0
                ? () => handleCardClick(index)
                : undefined
            }
          />
        ))}
      </Styled.CardStack>
    </Styled.Container>
  );
}
